"use client"

import { getBlurSVGPlaceholder } from "@/app/(site)/_components/Atoms/blurry-placeholder"
import { sanityBaseUrl } from "@/lib/sanityLoader"
import { buildImageUrl } from "@/sanity/lib/image"
import type { SanityImageSource } from "@sanity/image-url/lib/types/types"
import Image, { ImageProps } from "next/image"
import { useRef } from "react"

interface CentraImageObject {
    media: number
    url: string
}

interface BaseImageProps extends Omit<ImageProps, "src"> {
    src: SanityImageSource | string
    quality?: number
    mimeType?: string
    priority?: boolean
    sizes?: string
    // Add optional width and height for non-fill images
    width?: number
    height?: number
    asset?: {
        metadata?: {
            lqip?: string
        }
        width?: number
        height?: number
    }
    // Add support for normalized Centra image sources
    centraImageSources?: {
        small?: CentraImageObject[]
        standard?: CentraImageObject[]
        big?: CentraImageObject[]
        mini?: CentraImageObject[]
        full?: CentraImageObject[]
    }
    // Add prop to select specific image index from the array
    imageIndex?: number
}

export default function BaseImage({
    quality = 80,
    src,
    mimeType,
    priority = false,
    sizes = "100vw",
    width,
    height,
    asset,
    centraImageSources,
    imageIndex,
    ...props
}: BaseImageProps) {
    const imageRef = useRef<HTMLImageElement>(null)

    const isSanityImage =
        (typeof src === "string" && src.startsWith(sanityBaseUrl)) ||
        (typeof src === "string" && src.startsWith("image-"))

    const isCentraImage =
        centraImageSources &&
        typeof src === "string" &&
        (src.includes("centracdn") || src.includes("centraqa"))

    const blurUrl = getBlurSVGPlaceholder(
        asset?.metadata?.lqip || "",
        asset?.width || 1280,
        asset?.height || 853
    )

    // Handle image load to remove blurry placeholder
    const handleRemoveBlurPlaceholder = () => {
        if (imageRef.current) {
            const currentBackground = imageRef.current.style.backgroundImage
            if (currentBackground && currentBackground !== "none") {
                imageRef.current.style.backgroundImage = "none"
            }
        }
    }

    // Custom loader for Sanity images with responsive support
    const sanityLoader = ({ src, width }: { src: string; width: number }) => {
        if (!isSanityImage) return src

        const assetId = src.split("?")[0]
        let url =
            buildImageUrl(assetId)
                .width(width)
                .auto("format")
                .quality(quality)
                .fit("clip")
                .url() ?? ""

        if (mimeType === "image/gif") {
            url += `&h=${Math.round(width / 3)}`
        }

        return url
    }

    // Custom loader for Centra images that selects the appropriate size
    const centraLoader = ({ src, width }: { src: string; width: number }) => {
        if (!isCentraImage || !centraImageSources) return src

        // Determine which size to use based on width
        let selectedSize: string[] | undefined

        if (width <= 768) {
            selectedSize = centraImageSources.small?.map(obj => obj.url)
        } else if (width <= 1440) {
            selectedSize = centraImageSources.standard?.map(obj => obj.url)
        } else {
            selectedSize = centraImageSources.standard?.map(obj => obj.url)
        }

        // If the selected size doesn't exist, fallback to standard
        if (!selectedSize || selectedSize.length === 0) {
            selectedSize = centraImageSources.standard?.map(obj => obj.url)
        }

        // Use the specified imageIndex or default to 0
        const index = imageIndex !== undefined ? imageIndex : 0
        return selectedSize?.[index] || selectedSize?.[0] || src
    }

    // Choose the appropriate loader
    const customLoader = isSanityImage
        ? sanityLoader
        : isCentraImage
          ? centraLoader
          : undefined

    // For Centra images, use more specific breakpoints to reduce srcset entries
    // Since we only have 3 image sizes, we don't need many intermediate breakpoints
    const getOptimizedSizes = () => {
        if (!isCentraImage || !centraImageSources) return sizes

        // Use specific breakpoints that match our image sizes
        // This will generate fewer srcset entries
        return "(max-width: 768px) 50vw, (max-width: 1440px) 33vw, 25vw"
    }

    // Style with blurry placeholder as background (appears immediately when CSS is parsed)
    // The real image will naturally overlay the blurry placeholder when it loads
    // Only show the blurry placeholder if the image is a Sanity image and has an lqip
    const imageStyle = {
        ...(blurUrl &&
            isSanityImage &&
            asset?.metadata?.lqip && {
                backgroundImage: `url("${blurUrl}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat"
            })
    }

    // Determine if we need to use fill or explicit dimensions
    const useFill = props.fill !== undefined
    const imageProps = useFill ? { fill: true } : { width, height }

    // Determine if we should use unoptimized mode
    // Use unoptimized for static assets and other non-Sanity/non-Centra images
    const shouldUseUnoptimized = !isSanityImage && !isCentraImage

    return (
        <Image
            {...props}
            {...imageProps}
            ref={imageRef}
            onLoad={blurUrl && isSanityImage ? handleRemoveBlurPlaceholder : undefined}
            unoptimized={shouldUseUnoptimized}
            alt={props.alt || ""}
            src={typeof src === "string" ? src : ""}
            loader={customLoader}
            sizes={getOptimizedSizes()}
            priority={priority}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            style={{
                ...imageStyle,
                ...props.style
            }}
            className={props.className || ""}
        />
    )
}
